import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useArticleBySlug, useAuthorBySlug, usePublishedArticles } from "@/hooks/usePublicArticles";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";
import NewsletterBox from "@/components/feature/NewsletterBox";

const BASE_URL = "https://helvetik-journal.ch";

/* ─── Mobile Sticky Share Bar ─── */
function MobileStickyShare({ title, url }: { title: string; url: string }) {
  const [visible, setVisible] = useState(false);
  const shareText = encodeURIComponent(title);
  const shareUrl = encodeURIComponent(url);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-hj-gray-border px-3 py-2 flex items-center justify-between">
      <span className="text-xs font-medium text-hj-muted font-body">Teilen:</span>
      <div className="flex items-center gap-1.5">
        <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="Twitter">
          <i className="ri-twitter-x-line text-sm"></i>
        </a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="Facebook">
          <i className="ri-facebook-line text-sm"></i>
        </a>
        <a href={`https://wa.me/?text=${shareText}%20${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="WhatsApp">
          <i className="ri-whatsapp-line text-sm"></i>
        </a>
        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="LinkedIn">
          <i className="ri-linkedin-line text-sm"></i>
        </a>
      </div>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { article, loading, error } = useArticleBySlug(slug);
  const { author } = useAuthorBySlug(article?.authorSlug);
  const { articles: allArticles } = usePublishedArticles(20);

  const relatedArticles = allArticles.filter(
    (a) => a.id !== article?.id && a.categorySlug === article?.categorySlug
  ).slice(0, 3);

  const moreArticles = allArticles.filter((a) => a.id !== article?.id).slice(0, 3);

  // SEO meta injection
  useEffect(() => {
    if (!article) {
      document.title = "Artikel nicht gefunden | Helvetik Journal";
      return;
    }
    const canonicalUrl = article.canonicalUrl || `${BASE_URL}/artikel/${article.slug}`;
    const metaTitle = article.metaTitle || `${article.title} | Helvetik Journal`;
    const metaDesc = article.metaDescription || article.excerpt;
    const ogTitle = article.ogTitle || article.title;
    const ogDesc = article.ogDescription || article.excerpt;
    const ogImg = article.ogImage || article.imageUrl;
    const twitterImg = article.twitterImage || ogImg;

    document.title = metaTitle;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };

    setMeta("description", metaDesc);
    if (article.focusKeyword) setMeta("keywords", article.tags.join(", "));
    setLink("canonical", canonicalUrl);
    setMeta("og:type", "article", true);
    setMeta("og:title", ogTitle, true);
    setMeta("og:description", ogDesc, true);
    setMeta("og:image", ogImg, true);
    setMeta("og:url", canonicalUrl, true);
    setMeta("og:site_name", "Helvetik Journal", true);
    setMeta("article:published_time", article.publishedAt, true);
    if (article.updatedAt) setMeta("article:modified_time", article.updatedAt, true);
    setMeta("article:author", article.author, true);
    setMeta("article:section", article.category, true);
    article.tags.forEach((tag) => setMeta("article:tag", tag, true));
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:site", "@HelvetikJournal");
    setMeta("twitter:title", ogTitle);
    setMeta("twitter:description", ogDesc);
    setMeta("twitter:image", twitterImg);
    if (article.noIndex) setMeta("robots", "noindex, nofollow");

    const injectSchema = (id: string, data: object) => {
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
      el.textContent = JSON.stringify(data);
    };

    const wordCount = article.content.split(/\s+/).filter(Boolean).length;
    injectSchema("schema-newsarticle", {
      "@context": "https://schema.org", "@type": "NewsArticle",
      headline: article.title, description: metaDesc, image: [ogImg],
      datePublished: article.publishedAt, dateModified: article.updatedAt || article.publishedAt,
      author: { "@type": "Person", name: article.author, url: `${BASE_URL}/autor/${article.authorSlug}` },
      publisher: { "@type": "NewsMediaOrganization", name: "Helvetik Journal", url: BASE_URL, logo: { "@type": "ImageObject", url: `${BASE_URL}/logo.png`, width: 200, height: 60 } },
      mainEntityOfPage: canonicalUrl, keywords: article.tags.join(", "),
      articleSection: article.category, inLanguage: "de-CH", wordCount,
    });
    injectSchema("schema-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Startseite", item: BASE_URL },
        { "@type": "ListItem", position: 2, name: article.category, item: `${BASE_URL}/${article.categorySlug}` },
        { "@type": "ListItem", position: 3, name: article.title, item: canonicalUrl },
      ],
    });

    if (author) {
      injectSchema("schema-author", {
        "@context": "https://schema.org", "@type": "Person",
        name: author.name, url: `${BASE_URL}/autor/${author.slug}`,
        jobTitle: author.role, description: author.shortBio, image: author.image,
        sameAs: author.socialLinks.map((s) => s.url),
        worksFor: { "@type": "NewsMediaOrganization", name: "Helvetik Journal", url: BASE_URL },
      });
    }

    return () => {
      ["schema-newsarticle", "schema-breadcrumb", "schema-author"].forEach((id) => document.getElementById(id)?.remove());
    };
  }, [article?.slug, author]);

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  /* ─── Error ─── */
  if (error) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-hj-dark mb-2 font-heading">Fehler beim Laden</h1>
          <p className="text-hj-gray mb-4 font-body">{error}</p>
          <Link to="/" className="text-hj-red hover:underline font-body">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  /* ─── Not found ─── */
  if (!article) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-hj-dark mb-2 font-heading">Artikel nicht gefunden</h1>
          <p className="text-hj-gray mb-4 font-body">Der angeforderte Artikel existiert nicht oder wurde nicht veröffentlicht.</p>
          <Link to="/" className="text-hj-red hover:underline font-body">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  const isColumnArticle = article.categorySlug === "kolumnen" || article.categorySlug === "meinung" || article.isColumn || article.isOpinion;
  const contentParts = article.content.split("\n\n");
  const wordCount = article.content.split(/\s+/).filter(Boolean).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));
  const imageAlt = article.imageAlt || article.title;
  const shareUrl = typeof window !== "undefined" ? window.location.href : `${BASE_URL}/artikel/${article.slug}`;
  const shareText = encodeURIComponent(article.title);

  return (
    <div className="min-h-screen bg-white pt-[80px] md:pt-[130px] pb-16 md:pb-0">
      <MobileStickyShare title={article.title} url={shareUrl} />

      {/* Breadcrumb */}
      <nav className="max-w-4xl mx-auto px-3 md:px-6 pt-2 md:pt-6" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1.5 text-[11px] md:text-sm text-hj-muted font-body flex-wrap">
          <li><Link to="/" className="hover:text-hj-red transition-colors">Startseite</Link></li>
          <li><i className="ri-arrow-right-s-line text-[10px]"></i></li>
          <li><Link to={`/${article.categorySlug}`} className="hover:text-hj-red transition-colors">{article.category}</Link></li>
          <li><i className="ri-arrow-right-s-line text-[10px]"></i></li>
          <li className="text-hj-dark truncate max-w-[140px] md:max-w-[300px]" aria-current="page">{article.title}</li>
        </ol>
      </nav>

      {/* Article Header */}
      <header className={`max-w-4xl mx-auto px-3 md:px-6 py-3 md:py-8 ${isColumnArticle ? "border-l-3 border-hj-red pl-4 md:pl-8" : ""}`}>
        {isColumnArticle ? (
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-[10px] font-bold text-white bg-hj-red rounded-md uppercase tracking-wide">{article.category}</span>
            {article.categorySlug === "kolumnen" && (
              <span className="px-2 py-0.5 text-[10px] font-medium text-hj-red bg-hj-red/10 rounded">
                <i className="ri-user-star-line mr-1 text-[10px]"></i>Kolumne
              </span>
            )}
          </div>
        ) : (
          <span className="inline-block px-2 py-0.5 text-[10px] font-semibold text-white bg-hj-red rounded-md uppercase tracking-wide mb-2">{article.category}</span>
        )}

        <h1 className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-hj-dark leading-tight font-heading ${isColumnArticle ? "italic" : ""}`}>{article.title}</h1>

        {article.subtitle && (
          <p className="text-sm md:text-xl text-hj-gray mt-2 leading-relaxed font-body">{article.subtitle}</p>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-hj-muted font-body">
          <Link to={`/autor/${article.authorSlug}`} className="flex items-center gap-2 hover:text-hj-red transition-colors">
            {author?.image ? (
              <img src={author.image} alt={article.author} loading="lazy" width={28} height={28} className="w-7 h-7 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-hj-red/10 text-hj-red">
                {article.author.split(" ").map((n) => n[0]).join("")}
              </div>
            )}
            <span className="font-semibold text-hj-dark text-xs">{article.author}</span>
          </Link>
          <span className="hidden sm:inline w-1 h-1 rounded-full bg-hj-muted"></span>
          <time dateTime={article.publishedAt} className="hidden sm:inline text-[11px]">
            {new Date(article.publishedAt).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" })}
          </time>
          <time dateTime={article.publishedAt} className="sm:hidden text-[11px]">
            {new Date(article.publishedAt).toLocaleDateString("de-CH")}
          </time>
          <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
          <span className="text-[11px]">{readingTime} Min.</span>
          <span className="hidden sm:inline w-1 h-1 rounded-full bg-hj-muted"></span>
          <span className="hidden sm:inline text-[11px]">{article.views.toLocaleString("de-CH")} Aufrufe</span>
        </div>
      </header>

      {/* Hero Image */}
      <figure className="max-w-5xl mx-auto px-3 md:px-6">
        <div className="relative overflow-hidden rounded-lg md:rounded-xl aspect-[16/9]">
          <img src={article.imageUrl} alt={imageAlt} title={article.title} loading="eager" width={1200} height={675} className="w-full h-full object-cover" />
        </div>
        {article.imageAlt && (
          <figcaption className="text-[11px] text-hj-gray mt-1.5 text-center font-body px-2">{article.imageAlt}</figcaption>
        )}
      </figure>

      {/* Mobile ad after hero image */}
      <div className="md:hidden max-w-4xl mx-auto px-3 py-2">
        <AdSlotDisplay position="mobile_after_hero" />
      </div>

      {article.adsEnabled && (
        <div className="max-w-4xl mx-auto px-3 md:px-6 py-2 md:py-4">
          <AdSlotDisplay position="top_leaderboard" />
        </div>
      )}

      {/* Article Content */}
      <main className="max-w-4xl mx-auto px-3 md:px-6 pb-6 md:pb-12">
        <div className="flex flex-col lg:flex-row gap-6">
          <article className="flex-1 min-w-0">
            <p className="text-sm md:text-xl text-hj-gray leading-relaxed mb-4 md:mb-8 font-medium font-body">{article.excerpt}</p>

            {/* Mobile ad mid-article */}
            <div className="md:hidden my-4">
              <AdSlotDisplay position="mobile_article_mid" />
            </div>

            <div className={`space-y-4 font-body text-hj-dark ${isColumnArticle ? "text-base md:text-xl leading-[1.85]" : "text-[15px] md:text-lg leading-[1.8]"}`}>
              {contentParts.map((part, i) => {
                const elements = [];
                if (i === 2 && article.adsEnabled) {
                  elements.push(<div key={`ad-p2`} className="my-4 md:my-6"><AdSlotDisplay position="in_article_p2" /></div>);
                }
                if (i === 5 && article.adsEnabled) {
                  elements.push(<div key={`ad-p5`} className="my-4 md:my-6"><AdSlotDisplay position="in_article_p5" /></div>);
                }
                if (part.startsWith("<h2>")) {
                  const headingText = part.replace(/<\/?h2>/g, "");
                  elements.push(<h2 key={i} className={`text-lg md:text-2xl font-bold text-hj-dark mt-6 mb-2 font-heading ${isColumnArticle ? "border-l-4 border-hj-red pl-3" : ""}`}>{headingText}</h2>);
                } else if (part.startsWith("<blockquote>")) {
                  const quoteText = part.replace(/<\/?blockquote>/g, "");
                  elements.push(<blockquote key={i} className="border-l-3 border-hj-red pl-3 md:pl-5 py-1.5 my-4 md:my-6 bg-hj-gray-light/50 rounded-r-md"><p className={`italic text-hj-dark font-heading ${isColumnArticle ? "text-lg" : "text-base"}`}>{quoteText}</p></blockquote>);
                } else if (part.startsWith("<ul>")) {
                  const listItems = part.match(/<li>(.*?)<\/li>/g) || [];
                  elements.push(<ul key={i} className="list-disc list-inside space-y-1.5 my-3 ml-2">{listItems.map((item, idx) => <li key={idx} className="text-hj-dark text-sm md:text-base">{item.replace(/<\/?li>/g, "")}</li>)}</ul>);
                } else {
                  elements.push(<p key={i} className="text-hj-dark">{part}</p>);
                }
                return elements;
              })}
            </div>

            {/* Gallery */}
            {article.gallery && article.gallery.length > 0 && (
              <section className="mt-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-hj-dark mb-3 font-body">Bildergalerie</h3>
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  {article.gallery.map((img, i) => (
                    <figure key={i} className="rounded-lg overflow-hidden aspect-[16/10]">
                      <img src={img} alt={`Galeriebild ${i + 1} – ${article.title}`} loading="lazy" width={400} height={250} className="w-full h-full object-cover" />
                    </figure>
                  ))}
                </div>
              </section>
            )}

            {/* Sources */}
            {article.sources && article.sources.length > 0 && (
              <section className="mt-6 pt-4 border-t border-hj-gray-border">
                <h3 className="text-xs font-bold uppercase tracking-wider text-hj-dark mb-2 font-body">Quellen & Referenzen</h3>
                <ul className="space-y-1.5">
                  {article.sources.map((source, i) => (
                    <li key={i}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer nofollow" className="text-xs text-hj-red hover:underline font-body flex items-center gap-1">
                        <i className="ri-external-link-line text-[10px]"></i>{source.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Tags */}
            <section className="flex flex-wrap gap-1.5 mt-6 md:mt-10">
              {article.tags.map((tag) => (
                <Link key={tag} to={`/suche?q=${encodeURIComponent(tag)}`} className="px-2.5 py-1 text-xs text-hj-gray bg-hj-gray-light rounded-full hover:bg-hj-gray-border transition-colors font-body">#{tag}</Link>
              ))}
            </section>

            {/* Desktop Share */}
            <section className="hidden md:flex items-center gap-3 mt-8 pt-6 border-t border-hj-gray-border">
              <span className="text-sm font-medium text-hj-dark font-body">Teilen:</span>
              <a href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="Twitter"><i className="ri-twitter-x-line text-sm"></i></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="Facebook"><i className="ri-facebook-line text-sm"></i></a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="LinkedIn"><i className="ri-linkedin-line text-sm"></i></a>
              <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="WhatsApp"><i className="ri-whatsapp-line text-sm"></i></a>
              <a href={`mailto:?subject=${shareText}&body=${encodeURIComponent(shareUrl)}`} className="w-10 h-10 flex items-center justify-center rounded-full bg-hj-gray-light hover:bg-hj-red hover:text-white transition-colors" aria-label="E-Mail"><i className="ri-mail-line text-sm"></i></a>
            </section>

            {article.adsEnabled && (
              <div className="my-6 md:my-10"><AdSlotDisplay position="article_bottom" /></div>
            )}

            {/* Author Box */}
            <section className={`mt-8 md:mt-14 p-4 md:p-8 rounded-xl ${isColumnArticle ? "bg-hj-navy text-white" : "bg-white border border-hj-gray-border"}`}>
              <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
                {author ? (
                  <Link to={`/autor/${author.slug}`} className="shrink-0">
                    <div className="w-12 h-12 md:w-20 md:h-20 rounded-full overflow-hidden bg-hj-gray-light border-2 border-hj-gray-border">
                      {author?.image ? (
                        <img src={author.image} alt={article.author} loading="lazy" width={80} height={80} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-hj-gray">
                          <i className="ri-user-line text-xl md:text-2xl"></i>
                        </div>
                      )}
                    </div>
                  </Link>
                ) : (
                  <div className={`w-12 h-12 md:w-20 md:h-20 rounded-full flex items-center justify-center text-lg md:text-2xl font-bold shrink-0 border-2 ${isColumnArticle ? "bg-white/10 border-white/20 text-white" : "bg-hj-red/5 border-hj-red/10 text-hj-red"}`}>
                    {article.author.split(" ").map((n) => n[0]).join("")}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                      <Link to={`/autor/${article.authorSlug}`} className={`text-base md:text-xl font-bold hover:underline font-heading ${isColumnArticle ? "text-white" : "text-hj-dark hover:text-hj-red"}`}>{article.author}</Link>
                      <p className={`text-xs md:text-base mt-0.5 font-body ${isColumnArticle ? "text-white/60" : "text-hj-muted"}`}>{article.authorRole}</p>
                    </div>
                    <Link
                      to={`/autor/${article.authorSlug}`}
                      className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${
                        isColumnArticle
                          ? "bg-white/10 text-white hover:bg-white/20"
                          : "bg-hj-red text-white hover:bg-hj-red-light"
                      }`}
                    >
                      Profil ansehen
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                  <p className={`text-xs md:text-base mt-3 leading-relaxed font-body ${isColumnArticle ? "text-white/70" : "text-hj-gray"}`}>{author?.shortBio || article.authorBio}</p>
                  <div className="flex items-center gap-4 mt-3 flex-wrap">
                    <Link to={`/autor/${article.authorSlug}`} className={`inline-flex items-center gap-1.5 text-xs font-medium hover:underline font-body ${isColumnArticle ? "text-white/80" : "text-hj-red"}`}>
                      Alle Artikel
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                    {author?.email && (
                      <a href={`mailto:${author.email}`} className={`inline-flex items-center gap-1.5 text-xs font-medium hover:underline font-body ${isColumnArticle ? "text-white/60 hover:text-white" : "text-hj-gray hover:text-hj-dark"}`}>
                        <i className="ri-mail-line text-xs"></i>
                        Kontakt
                      </a>
                    )}
                  </div>
                  <Link
                    to={`/autor/${article.authorSlug}`}
                    className={`sm:hidden inline-flex items-center gap-1.5 px-3 py-2 mt-3 text-xs font-medium rounded-lg transition-colors ${
                      isColumnArticle
                        ? "bg-white/10 text-white"
                        : "bg-hj-red text-white"
                    }`}
                  >
                    Profil ansehen
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </section>

            {/* Newsletter */}
            <section className="mt-8 md:mt-14">
              <NewsletterBox variant="card" title="Bleiben Sie informiert." description="Erhalten Sie die wichtigsten Nachrichten aus der Schweiz direkt in Ihr Postfach." className="rounded-2xl" />
            </section>

            {/* Mobile ad before footer */}
            <div className="md:hidden mt-6">
              <AdSlotDisplay position="mobile_before_footer" />
            </div>
          </article>

          {/* Sidebar */}
          <aside className="hidden lg:block w-72 shrink-0 space-y-6">
            {article.adsEnabled && <AdSlotDisplay position="sidebar_top" />}
            {article.adsEnabled && <AdSlotDisplay position="sidebar_large" />}

            {author && (
              <div className="bg-white rounded-2xl border border-hj-gray-border p-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-hj-dark mb-5 font-body">Autor</h3>
                <div className="flex items-start gap-4">
                  <Link to={`/autor/${author.slug}`} className="shrink-0">
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-hj-gray-light border-2 border-hj-gray-border">
                      {author.image ? (
                        <img src={author.image} alt={author.name} loading="lazy" width={56} height={56} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-hj-gray">
                          <i className="ri-user-line text-xl"></i>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="min-w-0">
                    <Link to={`/autor/${author.slug}`} className="text-sm font-bold text-hj-dark hover:text-hj-red transition-colors font-heading block truncate">{author.name}</Link>
                    <p className="text-xs text-hj-muted font-body mt-0.5">{author.role}</p>
                  </div>
                </div>
                <p className="text-sm text-hj-gray mt-4 leading-relaxed font-body line-clamp-4">{author.shortBio}</p>
                <Link to={`/autor/${author.slug}`} className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 text-sm font-medium bg-hj-red text-white rounded-lg hover:bg-hj-red-light transition-colors font-body">
                  Profil ansehen
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <div className="bg-white rounded-2xl border border-hj-gray-border p-6">
                <h3 className="text-xs font-bold uppercase tracking-wider text-hj-dark mb-5 font-body">Verwandte Artikel</h3>
                <div className="space-y-5">
                  {relatedArticles.map((rel) => (
                    <Link key={rel.id} to={`/artikel/${rel.slug}`} className="group block">
                      <div className="w-full h-28 overflow-hidden rounded-lg mb-3">
                        <img src={rel.imageUrl} alt={rel.imageAlt || rel.title} loading="lazy" width={280} height={112} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                      <h4 className="text-sm font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{rel.title}</h4>
                      <time dateTime={rel.publishedAt} className="text-xs text-hj-muted mt-1.5 block font-body">{new Date(rel.publishedAt).toLocaleDateString("de-CH")}</time>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-hj-gray-border p-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-hj-dark mb-5 font-body">Schnellzugriff</h3>
              <div className="space-y-2.5">
                <Link to="/" className="flex items-center gap-2.5 text-sm text-hj-gray hover:text-hj-red transition-colors font-body"><i className="ri-home-line text-xs w-4 h-4 flex items-center justify-center"></i>Startseite</Link>
                <Link to={`/${article.categorySlug}`} className="flex items-center gap-2.5 text-sm text-hj-gray hover:text-hj-red transition-colors font-body"><i className="ri-folder-line text-xs w-4 h-4 flex items-center justify-center"></i>{article.category}</Link>
                <Link to="/kolumnisten" className="flex items-center gap-2.5 text-sm text-hj-gray hover:text-hj-red transition-colors font-body"><i className="ri-user-star-line text-xs w-4 h-4 flex items-center justify-center"></i>Kolumnisten</Link>
                <Link to="/kontakt" className="flex items-center gap-2.5 text-sm text-hj-gray hover:text-hj-red transition-colors font-body"><i className="ri-mail-line text-xs w-4 h-4 flex items-center justify-center"></i>Kontakt</Link>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="lg:hidden px-3 pb-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-hj-dark mb-3 font-body">Verwandte Artikel</h3>
          <div className="space-y-2">
            {relatedArticles.map((rel) => (
              <Link key={rel.id} to={`/artikel/${rel.slug}`} className="group flex gap-2.5">
                <div className="w-16 h-12 shrink-0 overflow-hidden rounded-md" style={{ minWidth: "64px" }}>
                  <img src={rel.imageUrl} alt={rel.imageAlt || rel.title} loading="lazy" width={64} height={48} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{rel.title}</h4>
                  <time dateTime={rel.publishedAt} className="text-[10px] text-hj-muted mt-0.5 block font-body">{new Date(rel.publishedAt).toLocaleDateString("de-CH")}</time>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* More Articles */}
      {moreArticles.length > 0 && (
        <section className="bg-hj-gray-light py-6 md:py-12">
          <div className="max-w-7xl mx-auto px-3 md:px-6">
            <h2 className="text-base md:text-2xl font-bold text-hj-dark mb-4 md:mb-6 font-heading">Das könnte Sie auch interessieren</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
              {moreArticles.map((rel) => (
                <Link key={rel.id} to={`/artikel/${rel.slug}`} className="group block bg-white rounded-xl overflow-hidden border border-hj-gray-border">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img src={rel.imageUrl} alt={rel.imageAlt || rel.title} title={rel.title} loading="lazy" width={400} height={250} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold text-white bg-hj-red rounded uppercase tracking-wide">{rel.category}</span>
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="text-sm md:text-base font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{rel.title}</h3>
                    <div className="flex items-center justify-between mt-2 text-xs text-hj-muted font-body">
                      <time dateTime={rel.publishedAt}>{new Date(rel.publishedAt).toLocaleDateString("de-CH")}</time>
                      <span>{Math.max(1, Math.ceil(rel.content.split(/\s+/).filter(Boolean).length / 200))} Min.</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}